import React, { useEffect, useMemo, useState } from "react"

import { Link, useLocation } from "react-router-dom"

import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config.ts"

import { LoginStep } from "../../config/auth.config"
import { GENERAL_CONFIG } from "../../config/general.config"
import { MENU_CONFIG } from "../../config/menu.config"
import {
    ROUTES_CONFIG,
    ROUTES_MENU_FREE_CONFIG,
} from "../../config/routes.config"
import { AuthHelper } from "../../helpers/auth.helper"
import { FeatureFlagsHelper } from "../../helpers/featureFlags.helper"
import { PermissionsHelper } from "../../helpers/permissions.helper"
import { useHasPermissions } from "../../hooks/hasPermissions.hook"
import { useAppDispatch } from "../../store/appDispatch.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { authSelector } from "../../store/selectors/auth.selector"
import { profileSelector } from "../../store/selectors/profile.selector"
import { runtimeSelector } from "../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../store/selectors/selectedPracticeRole.selector"
import { authSetLoginStep } from "../../store/slices/auth.slice"
import type {
    IEagleEyeViewAsConfig,
    IMenuItem,
} from "../../types/objects.types.ts"
import { ApiVersioningComponent } from "./apiVersioning.component.tsx"
import { EagleEyeComponent } from "./eagleEye.component"
import { AccountMenuComponent } from "./menu/accountMenu.component.tsx"
import { DesktopMenuComponent } from "./menu/desktopMenu.component"
import { MobileMenuComponent } from "./menu/mobileMenu.component"
import { SelectedPracticeComponent } from "./selectedPractice.component"

import logo from "@front-shared/assets/images/logo-small.svg"

export const HeaderComponent: React.FunctionComponent = () => {
    const location = useLocation()
    const dispatch = useAppDispatch()

    const runtime = useAppSelector(runtimeSelector)
    const authState = useAppSelector(authSelector)
    const profile = useAppSelector(profileSelector)
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    const [headerTopPosition, setHeaderTopPosition] = useState<number>(0)

    const hasEagleEyePermission = useHasPermissions(
        OneVerifyPermission.useEagleEye,
        undefined,
        false,
    )

    const [eagleEyeData] = useState<IEagleEyeViewAsConfig | null>(
        AuthHelper.getEagleEyeViewAsData(),
    )

    const isAllowedMenuItem = (item: IMenuItem): boolean => {
        // Check base feature flag requirement (applies to all conditions)
        const hasBaseFeatureFlag =
            !item.featureFlag ||
            FeatureFlagsHelper.isFeatureFlagEnabled(
                runtime.enabledFeatureFlags || [],
                item.featureFlag,
                item.featureFlagExistenceOperator,
            )

        if (!hasBaseFeatureFlag) {
            return false
        }

        // Check if user meets base permissions
        const hasBasePermissions =
            !item.neededPermissions ||
            PermissionsHelper.hasPermission(
                item.neededPermissions,
                selectedPracticeRole,
                item.neededPermissionsLogicalOperator,
            )

        // Check combined permissions (AND logic within each, OR logic between them)
        let hasCombinedPermissions = false

        if (item.combinedPermissions?.length) {
            hasCombinedPermissions = item.combinedPermissions.some(combined => {
                const combinedFeatureFlag =
                    !combined.featureFlag ||
                    FeatureFlagsHelper.isFeatureFlagEnabled(
                        runtime.enabledFeatureFlags || [],
                        combined.featureFlag,
                        combined.featureFlagExistenceOperator,
                    )

                const combinedPermissions =
                    !combined.neededPermissions ||
                    PermissionsHelper.hasPermission(
                        combined.neededPermissions,
                        selectedPracticeRole,
                        combined.neededPermissionsLogicalOperator,
                    )

                // Both feature flag AND permissions must be met for this combined permission
                return combinedFeatureFlag && combinedPermissions
            })
        }

        // Show if: (has base permissions) OR (has any combined permissions)
        return hasBasePermissions || hasCombinedPermissions
    }

    const allowedMenuItems = useMemo<IMenuItem[]>(() => {
        if (!runtime.enabledFeatureFlags) {
            return []
        }

        return (JSON.parse(JSON.stringify(MENU_CONFIG.mainMenu)) as IMenuItem[])
            .filter(item => {
                if (item.submenu?.length) {
                    item.submenu = item.submenu.filter(subItem => {
                        return isAllowedMenuItem(subItem)
                    })
                }

                const isMultipleSubmenu =
                    item.submenu?.length && item.submenu.length > 1

                return isMultipleSubmenu ? true : isAllowedMenuItem(item)
            })
            .map(item => {
                return item.submenu?.length === 1
                    ? {
                          ...item.submenu[0],

                          label: item.label,
                      }
                    : item
            })
    }, [selectedPracticeRole?.id, runtime.enabledFeatureFlags])

    const allowedAccountMenuItems = useMemo<IMenuItem[]>(() => {
        if (!runtime.enabledFeatureFlags) {
            return []
        }

        return MENU_CONFIG.accountMenu.filter(item => isAllowedMenuItem(item))
    }, [selectedPracticeRole?.id, runtime.enabledFeatureFlags])

    // When scrolling the page vertically -> make header hide itself by adjusting it's top position
    useEffect(() => {
        const onWindowScroll = () => {
            setHeaderTopPosition(-1 * window.scrollY)
        }

        window.addEventListener("scroll", onWindowScroll)

        return () => {
            window.removeEventListener("scroll", onWindowScroll)
        }
    }, [])

    return (
        <div
            style={{
                height: `${GENERAL_CONFIG.headerHeight}px`,
            }}
        >
            <div
                className="
                    fixed left-0 w-screen z-30
                    flex items-center py-[16px] px-[40px]
                    sm:block sm:px-[16px] md:pr-[0px]
                "
                style={{
                    top: `${headerTopPosition}px`,
                }}
            >
                <div className="mr-[56px]">
                    {runtime.isLoggedIn && (
                        <Link to={ROUTES_CONFIG.baseApplicationUrl}>
                            <img src={logo} alt="Nirvana" title="Nirvana" />
                        </Link>
                    )}

                    {!runtime.isLoggedIn &&
                        authState.loginStep === LoginStep.PASSWORD && (
                            <a href={GENERAL_CONFIG.mainWebsiteUrl}>
                                <img src={logo} alt="Nirvana" title="Nirvana" />
                            </a>
                        )}

                    {!runtime.isLoggedIn &&
                        authState.loginStep !== LoginStep.PASSWORD && (
                            <button
                                onClick={() => {
                                    dispatch(
                                        authSetLoginStep(LoginStep.PASSWORD),
                                    )
                                }}
                            >
                                <img src={logo} alt="Nirvana" title="Nirvana" />
                            </button>
                        )}
                </div>

                {runtime.isLoggedIn &&
                !~ROUTES_MENU_FREE_CONFIG.indexOf(location.pathname) &&
                profile ? (
                    <div className="flex items-center flex-1 sm:mt-[24px]">
                        <DesktopMenuComponent
                            menuItems={allowedMenuItems}
                            className="flex-1 mr-[24px]"
                        />

                        <div className="flex-1 hidden md:block sm:hidden" />

                        {hasEagleEyePermission ||
                        GENERAL_CONFIG.isStaging ||
                        GENERAL_CONFIG.isLocal ? (
                            <ApiVersioningComponent className="mr-[16px]" />
                        ) : null}

                        {hasEagleEyePermission || eagleEyeData ? (
                            <EagleEyeComponent className="mr-[16px]" />
                        ) : null}

                        {selectedPracticeRole ? (
                            <div className="flex items-center">
                                <SelectedPracticeComponent className="flex-1" />

                                <AccountMenuComponent
                                    menuItems={allowedAccountMenuItems}
                                    className="ml-[16px]"
                                />
                            </div>
                        ) : null}

                        <div className="flex-1 hidden sm:block" />

                        <MobileMenuComponent
                            menuItems={allowedMenuItems}
                            className="ml-[16px]"
                        />
                    </div>
                ) : null}
            </div>
        </div>
    )
}
